<template>
    <div class="mb-8">

        <running-auctions-banner
            v-if="runningAuctions.length > 0"
            :auctions="runningAuctions"
        />

        <websocket-watcher
            :channel="wsChannel"
            @message="handleMessage"
            @connected="setConnectedToRealtimeService"
        />
        <websocket-watcher
            :channel="internalWsChannel"
            @message="handleInternalMessage"
        />
        <page-headline title="test" v-if="viewState === 'control'">
            <Link :href="route('auctions')">{{ $t('pages.auctions.control.reverse_auction.auction_controls') }}</Link>
            <span class="font-medium">/</span>
            {{ auction.id }} ({{ auction.external_id }})
        </page-headline>
        <page-headline v-else>
            <Link :href="route('auctions')">{{ $t('pages.auctions.control.reverse_auction.auction_list') }}</Link>
        </page-headline>
        <div class="flex flex-col lg:flex-row justify-start controls-headline px-0 lg:px-5 mb-5 gap-x-2 md:gap-x-5 lg:gap-x-20">
            <div class="text-center mb-2 sm:mb-0 flex flex-row items-center w-full lg:w-1/3 justify-center lg:justify-start">
                {{ campaign.name }}
            </div>
            <div class="flex items-center justify-center lg:justify-between w-full gap-x-5">
                <div class="flex justify-start items-center gap-x-5 lg:gap-x-20 py-2">
                    <div class="flex gap-x-5">
                        <div @click="loadCampaign('prev')">
                            <icon name="arrow-left-circle" class="w-6 h-6 sm:w-7 sm:h-7 cursor-pointer"/>
                        </div>
                        <div @click="loadCampaign('next')">
                            <icon name="arrow-right-circle" class="w-6 h-6 sm:w-7 sm:h-7 cursor-pointer"/>
                        </div>
                    </div>
                    <div>
                        <div @click="showCampaignDetailsModal">
                            <icon
                                name="cog"
                                class="w-6 h-6 sm:w-7 sm:h-7 cursor-pointer"
                            />
                        </div>
                    </div>
                    <div v-if="campaign.manualStartAllowed === true && (campaign.running_auctions?.length ?? 0) === 0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6 sm:w-7 sm:h-7 cursor-pointer" @click="startCampaign()">
                            <path fill-rule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                  clip-rule="evenodd"/>
                        </svg>
                    </div>
                    <div v-if="campaign.isRerunCampaign === false && this.hasRunningReRunAuction === false && (campaign.running_auctions?.length ?? 0) === 0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 sm:w-7 sm:h-7 cursor-pointer"
                             @click="sendStartLiveShowEvent()">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M6 20.25h12m-7.5-3v3m3-3v3m-10.125-3h17.25c.621 0 1.125-.504 1.125-1.125V4.875c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125Z"/>
                        </svg>
                    </div>
                    <div>
                        <span @click="toggleAutoPilot()" class="text-white text-xs px-1 lg:px-3 py-1 w-full text-center cursor-pointer"
                              :class="{ 'bg-orange-600': !this.isAutoPilotActive, 'bg-green-600 animate-bounce': this.isAutoPilotActive }">
                            <span :class="{ 'animate-pulse': this.isAutoPilotActive }" class="text-white"
                                  v-if="this.isAutoPilotActive">{{ $t('pages.auctions.control.reverse_auction.autopilot_active') }}</span>
                            <span class="text-white" v-if="!this.isAutoPilotActive">{{ $t('pages.auctions.control.reverse_auction.autopilot_active') }}</span>
                        </span>
                    </div>
                </div>
                <div class="flex items-center justify-start gap-x-2">
                    <icon name="user" class="w-5 h-5 sm:w-7 sm:h-7 text-green-800"></icon>
                    {{ audience?.subscribers ?? 0 }}
                </div>
            </div>
        </div>
        <div class="w-full">
            <auction-flash-messages
                :messages="commandsMessages"
            />
        </div>
        <div class="flex flex-col md:flex-row gap-x-4 2xl:gap-x-8">
            <div class="overflow-hidden bg-gray-100" :class="{ 'w-full': this.viewState === 'list', 'w-full md:w-3/5 order-2 md:order-1': this.viewState !== 'list' }">
                <auction-flash-messages
                    v-if="messages.campaign"
                    :messages="messages.campaign"
                />
                <div class="flex flex-col overflow-y-auto">
                    <campaign-viewer
                        :campaign="this.campaign"
                        :active="auctionState"
                        :view="viewState"
                        @campaign:reload="loadCampaign(null);"
                        @aclick="manualView = null;"
                    ></campaign-viewer>
                </div>
            </div>
            <div class="flex" :class="{ 'hidden': this.viewState === 'list', 'w-full md:w-2/5 order-1 md:order-2 align-items-start': this.viewState !== 'list' }">
                <div class="sticky h-auto self-start top-20 xl:top-5 overflow-y-auto">
                    <div class="flex justify-between items-center bg-gray-300 mb-6 font-medium w-full py-4">
                        <div class="px-3">Steuerung</div>
                        <div class="px-3 cursor-pointer" @click="toggleView()">
                            <icon name="close"/>
                        </div>
                    </div>
                    <div class="w-full mb-6">
                        <div class="grid grid-cols-5 items-center justify-between align-middle">
                            <div>
                                <img
                                    :src="auction.image_url"
                                    class="w-1/2 h-auto cursor-pointer"
                                    @click="showProductImageModal"
                                    :alt="auction.name"
                                />
                            </div>
                            <div>
                                <span class="text-base md:text-sm lg:text-base">{{ auction.name }}</span><br/>
                                <span class="text-sm md:text-xs text-gray-500">{{ auction.article_id }}</span>
                            </div>
                            <div @click="showAuctionDetailsModal" class="cursor-pointer flex justify-center">
                                <icon
                                    name="information-circle"
                                    class="w-6 h-6"
                                />
                            </div>
                            <div @click="showProductDetailsModal(auction.article_id)" class="cursor-pointer flex justify-center">
                                <icon
                                    name="document-text"
                                    class="w-6 h-6"
                                />
                            </div>
                            <div class="flex justify-end flex justify-center">
                                <status
                                    :data="getAuctionState(auction)"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-if="auction.campaign?.isRerunCampaign === false" class="mb-6">
                        <div class="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 justify-center gap-2">
                            <auction-button
                                v-if="this.canPreview"
                                :in-progress="progressState.preview"
                                :wait-text="$t('pages.auctions.control.reverse_auction.please_wait')"
                                :color="'green'"
                                :disabled="!this.canPreview"
                                @click="handleAction('preview')"
                            >
                                <icon name="fire" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ this.isParked(auction) ? $t('pages.auctions.control.reverse_auction.reactivate') : $t('pages.auctions.control.reverse_auction.start_preview') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canStart"
                                :in-progress="progressState.start"
                                :wait-text="$t('pages.auctions.control.reverse_auction.activating')"
                                :color="'green'"
                                :disabled="!this.canStart"
                                @click="handleAction('start')"
                            >
                                <icon name="chevron-double-down" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.start_pricedrop') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canAdaptAmount"
                                :in-progress="progressState.amountIncrease"
                                :wait-text="$t('pages.auctions.control.reverse_auction.increasing_amount')"
                                :color="'orange'"
                                :disabled="!this.canAdaptAmount"
                                @click="increaseAmount()"
                            >
                                <icon name="arrow-up" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.increase_amount') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canActivatePriceTurbo"
                                :in-progress="progressState.priceTurbo"
                                :wait-text="$t('pages.auctions.control.reverse_auction.activating')"
                                :color="'orange'"
                                :disabled="!this.canActivatePriceTurbo"
                                @click="handleAction('priceTurbo')"
                            >
                                <icon name="fast-forward" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.price_turbo') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canGrantInstantDiscount"
                                :in-progress="progressState.instantDiscount"
                                :wait-text="$t('pages.auctions.control.reverse_auction.activating')"
                                :color="'orange'"
                                :disabled="!this.canGrantInstantDiscount"
                                @click="grantInstantDiscount"
                            >
                                <icon name="discount" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.instant_discount') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canPark"
                                :in-progress="progressState.park"
                                :wait-text="$t('pages.auctions.control.reverse_auction.parking')"
                                :color="'blue'"
                                :disabled="!this.canPark"
                                @click="handleAction('park')"
                            >
                                <icon name="clock" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.park') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canAbort"
                                :in-progress="progressState.abort"
                                :confirm-text="$t('pages.auctions.control.reverse_auction.cancel_auction')"
                                :wait-text="'Wird abgebrochen'"
                                :color="'red'"
                                :disabled="!this.canStop"
                                @click="handleAction('abort')"
                            >
                                <icon name="x" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.cancel') }}</span>
                            </auction-button>
                            <auction-button
                                v-if="this.canStop"
                                :in-progress="progressState.stop"
                                :confirm-text="$t('pages.auctions.control.reverse_auction.stop_auction')"
                                :wait-text="'Wird beendet'"
                                :color="'red'"
                                :disabled="!this.canStop"
                                @click="handleAction('stop')"
                            >
                                <icon name="stop" class="w-4 mr-2 md:hidden lg:flex"/>
                                <span>{{ $t('pages.auctions.control.reverse_auction.stop') }}</span>
                            </auction-button>
                        </div>
                    </div>
                    <div class="mb-6" v-if="runtimeData && this.connected">
                        <auction-runtime-data
                            v-if="runtimeData && this.connected"
                            :runtime="this.runtimeData"
                            :auction="this.auctionState"
                            :latest-socket-event="this.latestEvent"
                            :customer-locks="this.customerLocks"
                        />
                    </div>
                    <div>
                        <auction-data-viewer
                            @auction-validated="this.auctionValidated"
                            @auction-update="this.auctionUpdated"
                            :readOnly="auctionAttributesReadOnly"
                            :auction="auctionState"
                            :runtime="this.runtimeData"
                            :context="{ max_quantity: this.stocks.stock }"
                            :show-headlines="false"
                            :fields="visibleAuctionAttributeFields"
                        />
                    </div>
                </div>
            </div>
        </div>
        <modal
            ref="auctionDetailsModal"
            @confirm="hideAuctionDetailsModal"
            @cancel="hideAuctionDetailsModal"
            :dangerous="false"
            modalSize="l"
            title="Auktionsdetails"
            :show-cancel="false"
            label-confirm="Schließen"
        >
            <auction-data-viewer
                @auction-validated="this.auctionValidated"
                @auction-update="this.auctionUpdated"
                :readOnly="auctionAttributesReadOnly"
                :auction="auctionState"
                :runtime="runtime"
                :context="{ max_quantity: this.stocks.stock }"
                :show-headlines="false"
                :fields="[]"
                :cols="4"
            />
        </modal>
        <modal
            ref="campaignDetailsModal"
            @confirm="hideCampaignDetailsModal"
            @cancel="hideCampaignDetailsModal"
            :dangerous="false"
            modalSize="l"
            title="Kampagnendetails"
            :show-cancel="false"
            label-confirm="Schließen"
        >
            <campaign-data-viewer
                @campaign-validated="this.campaignValidated"
                @auction-update="auctionUpdated($event, false, 'campaign')"
                :readOnly="auctionAttributesReadOnly"
                :campaign="campaign"
                :show-headlines="false"
                :cols="1"
            />
        </modal>
        <modal
            ref="productDetailsModal"
            @confirm="hideProductDetailsModal"
            @cancel="hideProductDetailsModal"
            :dangerous="false"
            modalSize="l"
            title="Produktdetails"
            :show-cancel="false"
            label-confirm="Schließen"
            cols="1"
        >
            <product-details
                :in-progress="getProductDetailsProgressState"
                :details="productDetailsState"
                :auction="auction"
            />
        </modal>
        <modal
            ref="productImageModal"
            @confirm="hideProductImageModal"
            @cancel="hideProductImageModal"
            :dangerous="false"
            modalSize="m"
            title="Produktbild"
            :show-cancel="false"
            label-confirm="Schließen"
            cols="1"
        >
            <img v-if="auction.image_url !== null" :src="auction.image_url?.replace('403_403', '640_640')" class="w-full h-auto"/>
        </modal>
    </div>
</template>

<script>
import Modal from '@/Shared/Modals/Modal';
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import PageHeadline from "@/Shared/PageHeadline";
import Status from '@/Shared/Status';
import AuctionState from "@/mixins/AuctionState";
import WebsocketWatcher from "@/Shared/WebsocketWatcher";
import AuctionButton from "./Components/AuctionButton";
import AuctionDataViewer from "@/Shared/AuctionDataViewer";
import CampaignViewer from "./Components/CampaignViewer.vue";
import CampaignDataViewer from "@/Shared/CampaignDataViewer";
import AuctionListItem from "./Components/AuctionListItem";
import draggable from 'vuedraggable';
import AuctionRuntimeData from "./Components/AuctionRuntimeData";
import AuctionFlashMessages from "@/Shared/AuctionFlashMessages";
import FlashMessage from "@/Shared/FlashMessage";
import {Link} from '@inertiajs/vue2';
import moment from "moment";
import AdjustMaxPriceModal from "../../../Shared/Modals/AdjustMaxPriceModal.vue";
import ProductDetails from "./Components/ProductDetails.vue";
import RunningAuctionsBanner from "@/Pages/Campaigns/Components/RunningAuctionsBanner.vue";

export default {
    metaInfo() {
        return {
            title: moment(this.campaign.date_start).format('HH') + ' Uhr'
        }
    },

    mixins: [
        AuctionState
    ],

    layout: Layout,

    /**
     * Used components
     */
    components: {
        RunningAuctionsBanner,
        AuctionFlashMessages,
        ProductDetails,
        AdjustMaxPriceModal,
        Modal,
        CampaignViewer,
        CampaignDataViewer,
        Icon,
        PageHeadline,
        Status,
        WebsocketWatcher,
        AuctionButton,
        AuctionDataViewer,
        AuctionListItem,
        draggable,
        AuctionRuntimeData,
        FlashMessage,
        Link
    },

    props: {

        runningAuctions: Array,

        /**
         * Current auction
         */
        auction: Object,

        /**
         * Current runtime data (if available)
         */
        runtime: Object,

        /**
         * Selected campaign
         */
        campaign: Object,

        /**
         * Websocket Channel to listen to
         */
        wsChannel: String,

        /**
         * Id of the websocket channel for internal messages
         */
        internalWsChannel: {
            type: String,
            default: null
        },

        /**
         * Stored stocks for the auction article
         */
        stocks: {
            type: Object,
            default: null
        },

        /**
         * Audience (websocket channel) stats
         */
        audience: {
            type: Object,
            default: null
        },

        messages: {
            type: Object,
            default: null
        },

        acl: {
            type: Object,
            default: {}
        },

        hasRunningReRunAuction: {
            type: Boolean,
            default: false
        },

        view: {
            type: String,
            default: 'list'
        },

        customerLocks: {
            type: Array,
            default: []
        }

    },
    data() {
        return {

            /**
             * Product Details
             */
            productDetailsState: null,

            /**
             * View Identifier
             */
            manualView: null,

            /**
             * Connection to ably established? Being set by event from websocketWatcher component
             */
            connected: false,

            /**
             * Campaign loading in progress?
             */
            loadingCampaign: false,

            /**
             * Stores the updated runtime data
             */
            updatedRuntimeData: false,

            /**
             * Is the basic auction data valid?
             */
            dataIsValid: false,

            /**
             * Is the basic campaign data valid?
             */
            campaignDataIsValid: false,

            /**
             * Progress state of the different auction actions
             */
            progressState: {
                'preview': false,
                'start': false,
                'abort': false,
                'amountIncrease': false,
                'priceTurbo': false,
                'instantDiscount': false,
                'getProductDetails': false,
                'stop': false,
                'park': false
            },

            /**
             * Stores the name of the latest received event
             */
            latestEvent: null,

            commandsMessages: {
                info: [],
                success: [],
                error: []
            },

            /**
             *
             */
            quickUpdatesQueue: [],

            quickUpdateQueued: false
        }
    },

    computed: {

        visibleAuctionAttributeFields() {
            return [
                'article_quantity',
                'start_price',
                'end_price',
                'pricedrop_interval',
                'pricedrop_amount',
                'attribute_reference_price',
                'attribute_purchase_price',
                'attribute_avg',
                'webshop_price',
                'auction_min_price'
            ];
        },

        /**
         * Current auctions state
         */
        auctionState() {
            return {
                ...this.auction,
                start_price: parseFloat(this.auction.start_price),
                pricedrop_amount: parseFloat(this.auction.pricedrop_amount),
                article_quantity: parseInt(this.auction.article_quantity),
                preview_duration: parseInt(this.auction.preview_duration),
                trail_duration: parseInt(this.auction.trail_duration),
                attributes: {
                    lfd_nr: parseInt(this.auction.attributes.lfd_nr),
                    reference_price: parseFloat(this.auction.attributes.reference_price),
                    avg: parseFloat(this.auction.attributes.avg),
                    purchase_price: parseFloat(this.auction.attributes.purchase_price),
                    webshop_price: parseFloat(this.auction.attributes.webshop_price)
                }
            };
        },

        audienceState() {
            return this.audience;
        },

        /**
         * Can the current auction be put into preview state (2)?
         */
        canPreview: function () {
            return (this.canBeEdited(this.auctionState) || this.isParked(this.auctionState)) && this.dataIsValid;
        },

        /**
         * Can the current auction be put into running state (3)?
         */
        canStart: function () {
            return (this.isPreview(this.auctionState));
        },

        /**
         * Can the amount of the current auction be adapted?
         */
        canAdaptAmount: function () {
            return (this.isRunning(this.auctionState) || this.isPreview(this.auctionState));
        },

        /**
         * Can the price turbo for the current auction be initiated?
         */
        canActivatePriceTurbo: function () {
            if (!this.runtimeData || !this.isRunning(this.auctionState)) {
                return false;
            }
            return !this.isPriceTurboActive(this.runtimeData) && !this.isPriceTurboInitiated(this.runtimeData);
        },

        /**
         * Can an instant discount be granted?
         */
        canGrantInstantDiscount: function () {
            if (!this.runtimeData || !this.isRunning(this.auctionState)) {
                return false;
            }
            return !this.isInstantDiscountGranted(this.runtimeData);
        },

        /**
         * Can the current auction be stopped?
         */
        canStop: function () {
            return (this.isRunning(this.auctionState) || this.isPreview(this.auctionState) || this.isParked(this.auctionState));
        },

        canAbort: function() {
            return (this.isRunning(this.auctionState) || this.isPreview(this.auctionState));
        },

        /**
         * Can the auction be parked?
         *
         * @returns {boolean}
         */
        canPark: function() {
            return (this.isPreview(this.auctionState));
        },

        /**
         * Is the auto pilot for the current campaign active?
         */
        isAutoPilotActive: function () {
            return (this.campaign.auctions_auto_play === 1);
        },

        /**
         * Current auctions runtime data
         */
        runtimeData: function () {
            if (!this.updatedRuntimeData) {
                return this.runtime;
            }
            return this.updatedRuntimeData;
        },

        /**
         * Calculates the max grantable instant discount
         */
        maxInstantDiscountAmount: function () {
            if (this.runtimeData) {
                return Math.max(0, Math.floor((this.runtimeData.current_price - 1) - this.runtimeData.price_drop_amount));
            }
            return 0;
        },

        /**
         * Check read only state of auction attributes
         */
        auctionAttributesReadOnly: function () {
            return !(this.acl.auctionControlHandle === true) || (this.campaign.template_id !== 2 && this.campaign.template_id !== 4);
        },

        viewState: function () {
            return this.manualView !== null ? this.manualView : this.view;
        },

        getProductDetailsProgressState() {
            return this.progressState.getProductDetails;
        }
    },

    methods: {

        async getProductDetails(productId) {

            let self = this;
            this.progressState.getProductDetails = true;

            axios.get(route('auctions.details', {auction: this.auctionState.id}))
                .then((result) => {
                    self.progressState.getProductDetails = false;
                    self.productDetailsState = result.data;
                })
                .catch((err) => console.error(err));
        },

        toggleView() {
            if (this.viewState === 'list') {
                this.manualView = 'control';
            } else {
                this.manualView = 'list';
            }
        },

        showAuctionDetailsModal() {
            this.$refs.auctionDetailsModal.show();
        },

        hideAuctionDetailsModal() {
            this.$refs.auctionDetailsModal.hide();
        },

        showCampaignDetailsModal() {
            this.$refs.campaignDetailsModal.show();
        },

        hideCampaignDetailsModal() {
            this.$refs.campaignDetailsModal.hide();
        },

        showProductDetailsModal(productId) {
            this.getProductDetails(productId);
            this.$refs.productDetailsModal.show();
        },

        hideProductDetailsModal() {
            this.$refs.productDetailsModal.hide();
        },

        showProductImageModal() {
            this.$refs.productImageModal.show();
        },

        hideProductImageModal() {
            this.$refs.productImageModal.hide();
        },

        resetCommandMessages() {
            this.commandsMessages = {success: [], info: [], error: []}
        },

        printCampaign(campaignId) {
            window.open(
                route('campaigns.print', {campaign: campaignId}),
                "_blank"
            );
        },

        /**
         * Being called after an auction has been validated
         */
        auctionValidated(valid) {
            this.dataIsValid = valid;
        },

        campaignValidated(valid) {
            this.campaignDataIsValid = valid;
        },

        /**
         * Being called after an auction has been updated
         */
        auctionUpdated(t, skipArticleQuantity = false, context = 'auction') {

            var self = this;

            if (t.name === 'article_quantity' && !skipArticleQuantity) {
                this.changeAuctionQuantity(t);
                return;
            }

            this.quickUpdatesQueue.push({
                name: t.name,
                val: t.value
            });

            function getRoute(postData) {
                if (context === 'campaign') {
                    return axios.post(route('campaigns.quickUpdate', {campaign: self.campaign.id}), {campaignData: postData});
                }
                return axios.post(route('auctions.quickUpdate', {auction: self.auctionState.id}), {auctionData: postData});
            }

            function getReloadProps() {
                if (context === 'campaign') {
                    return ['campaign'];
                }
                return ['auction', 'campaign', 'stocks'];
            }

            if (!this.quickUpdateQueued) {
                setTimeout(function () {
                    const postData = [...this.quickUpdatesQueue];
                    this.quickUpdatesQueue = [];
                    this.quickUpdateQueued = false;
                    getRoute(postData)
                        .then((resp) => {
                            if (self.canStop === false) {
                                self.$inertia.reload({
                                    preserveScroll: true,
                                    preserveState: true,
                                    only: getReloadProps()
                                })
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                        })
                }.bind(self), 100);
                this.quickUpdateQueued = true;
            }
        },

        /**
         * Change the auction quantity. Triggers prompt
         */
        changeAuctionQuantity(t) {
            let obj = this;
            this.$inertia.reload({
                preserveScroll: true,
                only: ['stocks'],
                onSuccess: page => {

                    let increaseAmount = 0;

                    if (t === null) {
                        increaseAmount = parseInt(window.prompt(this.$t('pages.auctions.control.reverse_auction.additional_amount_stock', {stock: page.props.stocks.stock}), ''));
                    } else {
                        // Calculate delta
                        increaseAmount = parseInt(t.value) - this.auctionState.article_quantity;
                    }

                    if (isNaN(increaseAmount) || 0 === increaseAmount || increaseAmount > parseInt(page.props.stocks.stock)) {
                        obj.progressState.amountIncrease = false;
                        return;
                    }

                    axios.post(route('auctions.amount', {auction: obj.auction.id}), {amount: increaseAmount})
                        .then((resp) => {
                            if (resp.data.status === 0) {
                                if (t !== null) {
                                    obj.auctionUpdated(t, true);
                                }
                            } else {
                                obj.commandsMessages.error.push('Diese Menge ist leider nicht mehr verfügbar. Die noch zur Verfügung stehende Menge wurde aktualisiert.');
                                obj.auction.article_quantity += 1;
                                this.$inertia.reload({
                                    only: ['stocks', 'auction']
                                });
                            }
                            obj.progressState.amountIncrease = false;
                        })
                        .catch((err) => {
                            console.error(err);
                        })

                },
                onCancel: page => {
                    console.log("CANCEL", page);
                }
            })
        },

        /**
         * Toggle the autopilot for the current campaign
         */
        toggleAutoPilot() {
            let obj = this;
            if (obj.campaign.isRerunCampaign === false) {
                axios.post(route('campaigns.toggle_auto_pilot', {campaign: obj.campaign.id}))
                    .then((resp) => {
                        if (resp.data.data.status === 0) {
                            this.$inertia.reload({
                                only: ['messages', 'campaign', 'auction']
                            });
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                    })
            }
        },

        /**
         * Load campaign data
         */
        loadCampaign(cmd) {
            this.loadingCampaign = true;
            let oldCampaign = {...this.campaign};
            this.campaign.auctions = [];
            this.$inertia.reload({
                data: {
                    'currentCampaign': this.campaign.id,
                    'cmd': cmd
                },
                only: ['campaign', 'messages'],
                onSuccess: page => {
                    if (page.props.campaign === null) {
                        page.props.campaign = oldCampaign;
                    }
                    this.loadingCampaign = false;
                    document.getElementById('campaigns__list').scrollTop = 0;
                }
            })
        },

        /**
         * Dummy
         */
        async handleInternalMessage(e) {
            switch (e.name) {
                case 'WebsocketChannelStatsUpdated':
                    this.$inertia.reload({
                        only: ['audience']
                    });
                    break;
            }
        },

        handleMessage(e) {
            if (e.name.match(/^Auction/) === null || e.data.auction?.campaign_id !== this.auctionState?.campaign?.id) {
                return;
            }
            let reload = true;
            let updatedCountdown = false;
            switch (e.name) {
                case 'AuctionExpired':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.abort = false;
                        this.progressState.stop = false;
                    }
                    break;
                case 'AuctionParked':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.park = false;
                    }
                    break;
                case 'AuctionStarted':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.start = false;
                        updatedCountdown = true;
                    }
                    break;
                case 'AuctionTrailing':
                case 'AuctionStopped':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.abort = false;
                        this.progressState.stop = false;
                    }
                    break;
                case 'AuctionPreviewStarted':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.preview = false;
                    } else if (e.data.auction.campaign_id === this.campaign.id) {
                        this.$inertia.get(route('auctions.control', {auction: e.data.auction.id}));
                        return;
                    }
                    break;
                case 'AuctionAmountAdapted':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.amountIncrease = false;
                        reload = false;
                    }
                    break;
                case 'AuctionPriceTurboActivated':
                    if (e.data.auction.id === this.auctionState.id) {
                        this.progressState.priceTurbo = false;
                        reload = false;
                    }
                    break;
                case 'AuctionPriceDrop':
                    reload = false;
                    updatedCountdown = true;
                    break;
                case 'AuctionBidListUpdated':
                    this.$inertia.reload({
                        preserveScroll: true,
                        only: ['auction']
                    });
                    reload = false;
                    break;
                case '':
                    break;
            }
            if (e.data.auction.runtime) {
                this.latestEvent = e.name;
                // Check if the message is for the currently selected auction
                if (e.data.auction.runtime.auction && e.data.auction.id === this.auctionState.id) {
                    this.updatedRuntimeData = e.data.auction.runtime.auction
                }
            }
            if (reload) {
                this.$inertia.reload({preserveScroll: true});
            }
        },

        setConnectedToRealtimeService(status) {
            this.connected = status;
        },

        /**
         * Handle different type of auction actions
         *
         * @param type
         * @param auctionId
         * @returns {Promise<void>}
         */
        async handleAction(type, auctionId = null) {
            let obj = this;
            let routeName = `auctions.${type}`;
            let useAuctionId = null === auctionId ? obj.auction.id : auctionId;
            this.progressState[type] = true;
            this.resetCommandMessages();
            axios.post(route(routeName, {auction: useAuctionId}))
                .then((response) => {
                    if (response.data.status === 1) {
                        obj.commandsMessages.error.push(response.data.message);
                        obj.progressState[type] = false;
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        async startCampaign() {
            await this.handleAction('preview', this.campaign?.auctions[0].id);
        },

        async sendStartLiveShowEvent() {
            axios.post(route('auctions.start-live-show', {auction: this.auction.id}))
                .then((response) => {
                    if (response.data.status === 1) {
                        obj.commandsMessages.error.push(response.data.message);
                        obj.progressState[type] = false;
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        /**
         * Increase the available auction amount during runtime
         *
         * @returns {Promise<void>}
         */
        async increaseAmount() {
            this.progressState.amountIncrease = true;
            this.changeAuctionQuantity(null);
        },

        async grantInstantDiscount() {

            let obj = this;
            this.progressState.instantDiscount = true;

            let instantDiscountAmount = parseFloat(window.prompt(this.$t('pages.auctions.control.reverse_auction.additional_discount', {amount: this.maxInstantDiscountAmount}), ''));

            if (!isNaN(instantDiscountAmount) && instantDiscountAmount <= this.maxInstantDiscountAmount) {
                axios.post(route('auctions.instantDiscount', {auction: obj.auction.id}), {amount: instantDiscountAmount})
                    .then((response) => {
                        obj.progressState.instantDiscount = false;
                    })
                    .catch((err) => {
                        console.error(err);
                    })
            } else {
                this.progressState.instantDiscount = false;
            }
        }
    },
    watch: {
        auctionState: function (a, b) {
            if (a.id !== b.id) {
                this.updatedRuntimeData = null;
            }
        }
    }
}
</script>
